import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import { getEntitlementsSection } from '../../utils/entitlements';
import { formatDateTime } from '../../utils/formatUtils';
import './css/ReportsListPage.css';

const ReportsListPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const reportApi = new DataApi('reports');
      const response = await reportApi.get('/');

      if (response.data.success) {
        setReports(response.data.data);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Error loading reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = () => {
    navigate('/report-builder');
  };

  const handleEditReport = (reportId) => {
    navigate(`/reportbuilderpage/${reportId}`);
  };

  const handleViewReport = (reportId) => {
    navigate(`/viewreport/${reportId}?source=reports`);
  };

  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const confirmDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      const reportApi = new DataApi('reports');
      await reportApi.delete(reportToDelete.id);

      // Refresh the reports list
      await fetchReports();

      // alert('Report deleted successfully');
      console.log('Report deleted successfully');
    } catch (err) {
      console.error('Error deleting report:', err);
      // alert('Error deleting report. Please try again.');
    } finally {
      setReportToDelete(null);
    }
  };

  const reportEntitlements = getEntitlementsSection('report', {});
  const allowCreateReport = reportEntitlements.allowCreateReport !== false;
  const maxReports = Number(reportEntitlements.maxReports);
  // Only enforce a cap when maxReports is a positive number. maxReports === 0 used to mean "default from merge" and wrongly blocked all creation (0 >= 0).
  const isReportLimitReached =
    Number.isFinite(maxReports) && maxReports > 0 && reports.length >= maxReports;
  const canCreateReport = allowCreateReport && !isReportLimitReached;

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.object_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (report.object_label && report.object_label.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterType === 'all' ||
      (filterType === 'public' && report.is_public) ||
      (filterType === 'private' && !report.is_public) ||
      (filterType === 'tabular' && report.report_type === 'tabular') ||
      (filterType === 'chart' && report.report_type === 'chart');

    return matchesSearch && matchesFilter;
  });

  const formatReportDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'tabular':
        return '📊';
      case 'chart':
        return '📈';
      case 'summary':
        return '📋';
      case 'matrix':
        return '🔢';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <div className="reports-list-page">
        <div className="reports-list-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-list-page">
        <div className="reports-list-container">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error Loading Reports</h3>
            <p>{error}</p>
            <button onClick={fetchReports} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-list-page">
      <div className="reports-list-container">
        <div className="reports-header">
          <div className="header-content">
            <h1>Reports</h1>
            <p>Manage and view all your reports</p>
          </div>
          {canCreateReport && (
            <button
              className="create-report-btn"
              onClick={handleCreateReport}
            >
              <span className="btn-icon">+</span>
              Create New Report
            </button>
          )}
        </div>

        <div className="reports-filters-row">
          <div className="reports-filters">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search reports by name, description, or object labels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-container">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Reports</option>
                <option value="public">Public Reports</option>
                <option value="private">Private Reports</option>
                <option value="tabular">Tabular Reports</option>
                <option value="chart">Chart Reports</option>
              </select>
            </div>
          </div>

          <div className="reports-stats">
            <div className="stat-item">
              <span className="stat-number">{reports.length}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{reports.filter(r => r.is_public).length}</span>
              <span className="stat-label">Public</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{filteredReports.length}</span>
              <span className="stat-label">Filtered</span>
            </div>
          </div>
        </div>

        {filteredReports.length === 0 ? (
          <div className="no-reports-container">
            <div className="no-reports-icon">📊</div>
            <h3>No Reports Found</h3>
            <p>
              {searchTerm || filterType !== 'all'
                ? 'No reports match your current filters. Try adjusting your search criteria.'
                : 'You haven\'t created any reports yet. Create your first report to get started.'
              }
            </p>
            {(!searchTerm && filterType === 'all') && canCreateReport && (
              <button
                className="create-first-report-btn"
                onClick={handleCreateReport}
              >
                Create Your First Report
              </button>
            )}
          </div>
        ) : (
          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Object</th>
                  <th>Type</th>
                  <th>Visibility</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="report-row">
                    <td className="report-name-cell">
                      <div className="report-name-content">
                        <span className="report-type-icon">
                          {getReportTypeIcon(report.report_type)}
                        </span>
                        <div>
                          <div className="report-name">{report.report_label || report.name}</div>
                          {report.description && (
                            <div className="report-description">
                              {report.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="object-cell">
                      {report.report_objects && report.report_objects.length > 0 ? (
                        // Show all objects from report_objects array (including primary)
                        report.report_objects.map((obj, index) => (
                          <span key={obj.id || index}>
                            {obj.object_label || obj.object_name}
                            {index < report.report_objects.length - 1 && ' & '}
                          </span>
                        ))
                      ) : (
                        // Fallback to primary object only
                        report.object_label || report.object_name
                      )}
                    </td>
                    <td className="type-cell">
                      <span className="type-badge">{report.report_type}</span>
                    </td>
                    <td className="visibility-cell">
                      {report.is_public ? (
                        <span className="visibility-badge public">Public</span>
                      ) : (
                        <span className="visibility-badge private">Private</span>
                      )}
                    </td>
                    <td className="date-cell">
                      {formatReportDate(report.createddate)}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleViewReport(report.id)}
                          title="View Report"
                        >
                          👁️
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditReport(report.id)}
                          title="Edit Report"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteReport(report)}
                          title="Delete Report"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setReportToDelete(null);
        }}
        onConfirm={confirmDeleteReport}
        title="Confirm Delete"
        message="Are you sure you want to delete this report?"
        itemName={reportToDelete?.name}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ReportsListPage;
